import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Gift, History, Repeat } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { toLocalIso } from "../../lib/dateUtils";
import { useAuth } from "../../context/AuthContext";
import ClientPromoBanner from "../../components/ClientPromoBanner";
import PendingApprovalsBanner from "../../components/PendingApprovalsBanner";
import { VipBadge, VipProgress } from "../../components/VipBadge";
import { Brand } from "../../context/CompanyContext";

export default function ClientDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [promo, setPromo] = useState(null);
  const [refData, setRefData] = useState(null);
  const [lastBooking, setLastBooking] = useState(null);
  const [repeating, setRepeating] = useState(false);
  const [vipStatus, setVipStatus] = useState(null);

  useEffect(() => {
    api.get("/bookings").then(({ data }) => setBookings(data.items)).catch(() => {});
    api.get("/invoices").then(({ data }) => setInvoices(data.items)).catch(() => {});
    api.get("/promotion/public").then(({ data }) => setPromo(data)).catch(() => {});
    api.get("/client/referrals").then(({ data }) => setRefData(data)).catch(() => {});
    api.get("/client/last-booking").then(({ data }) => setLastBooking(data.last)).catch(() => {});
    api.get("/client/vip-status").then(({ data }) => setVipStatus(data)).catch(() => {});
  }, []);

  async function repeatLastBooking() {
    if (!lastBooking) return;
    const date = window.prompt(
      "Em que dia quer repetir esta limpeza?\n\nFormato: AAAA-MM-DD (ex.: 2026-05-15)",
      toLocalIso(new Date(Date.now() + 7 * 86400000)),
    );
    if (!date) return;
    const time = window.prompt("A que horas? Formato HH:MM", lastBooking.scheduled_time || "10:00");
    if (!time) return;
    setRepeating(true);
    try {
      await api.post("/client/repeat-last", {
        scheduled_date: date, scheduled_time: time,
      });
      toast.success("Limpeza agendada! Verifique em 'Minhas reservas'.");
      const { data } = await api.get("/bookings");
      setBookings(data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setRepeating(false); }
  }

  const upcoming = bookings.filter(b => ["pending", "confirmed", "in_progress"].includes(b.status)).slice(0, 5);
  const pendingCount = bookings.filter(b => ["pending", "confirmed", "in_progress"].includes(b.status)).length;
  const completedCount = bookings.filter(b => b.status === "completed").length;

  return (
    <div className="space-y-8 md:space-y-12">
      <div>
        <div className="eyebrow mb-2 md:mb-3">Bom dia, {user?.name?.split(" ")[0]}</div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-3xl md:text-5xl">Painel do host.</h1>
          {vipStatus?.is_vip && <VipBadge size="md" />}
        </div>
      </div>

      <ClientPromoBanner />

      <PendingApprovalsBanner role="client" />

      <VipProgress status={vipStatus} />

      {/* Quick actions: timeline + repeat last */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="client-quick-actions">
        <Link to="/cliente/historico"
              className="lux-card p-5 flex items-center gap-4 hover:border-[#d4af37] transition-colors"
              data-testid="quick-timeline">
          <div className="w-12 h-12 rounded-sm bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] shrink-0">
            <History size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow mb-1 text-[#d4af37]">Linha do tempo</div>
            <div className="text-sm">Veja o histórico de limpezas com fotos.</div>
          </div>
          <ChevronRight size={18} className="text-[#a0b2af] shrink-0" />
        </Link>

        <button onClick={repeatLastBooking} disabled={!lastBooking || repeating}
                className="lux-card p-5 flex items-center gap-4 text-left hover:border-[#d4af37] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="quick-repeat-last">
          <div className="w-12 h-12 rounded-sm bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] shrink-0">
            <Repeat size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow mb-1 text-[#d4af37]">Repetir última limpeza</div>
            <div className="text-sm truncate">
              {lastBooking
                ? `${lastBooking.apt_type} · ${lastBooking.scheduled_date}`
                : "Sem reservas anteriores."}
            </div>
          </div>
          <ChevronRight size={18} className="text-[#a0b2af] shrink-0" />
        </button>
      </div>

      {/* MOBILE QUICK STATS + CTA */}
      <div className="md:hidden space-y-3" data-testid="client-mobile-stats">
        <div className="grid grid-cols-2 gap-3">
          <Link to="/cliente/reservas"
                className="lux-card p-4 text-center hover:border-[#d4af37] transition-colors"
                data-testid="client-mobile-stat-reservas">
            <div className="eyebrow text-[10px]">Reservas</div>
            <div className="font-display text-3xl text-[#d4af37] mt-1">{pendingCount}</div>
          </Link>
          <Link to="/cliente/historico"
                className="lux-card p-4 text-center hover:border-[#d4af37] transition-colors"
                data-testid="client-mobile-stat-historico">
            <div className="eyebrow text-[10px]">Histórico</div>
            <div className="font-display text-3xl text-[#d4af37] mt-1">{completedCount}</div>
          </Link>
        </div>
        <Link to="/cliente/agendar" className="btn-gold w-full py-3 text-sm inline-flex items-center justify-center gap-2"
              data-testid="client-mobile-cta">
          Agendar limpeza <ChevronRight size={16} />
        </Link>
      </div>

      {promo?.active && (
        <div className="lux-card p-6 flex items-center justify-between" data-testid="promo-card">
          <div>
            <div className="eyebrow mb-2">Promoção ativa</div>
            <div className="font-display text-2xl">30 dias de preços especiais</div>
            <div className="text-sm text-[#a0b2af] mt-1">Contacte a <Brand /> para o valor da sua tipologia.</div>
          </div>
          <Link to="/cliente/agendar" className="btn-gold px-6 py-3 text-sm inline-flex items-center gap-2">
            Agendar <ChevronRight size={16} />
          </Link>
        </div>
      )}

      {refData && (
        <Link to="/cliente/indicacoes"
              className="lux-card p-5 flex items-center gap-4 border-[#d4af37]/30 hover:border-[#d4af37]/60 transition-colors"
              data-testid="client-referral-mini-card">
          <div className="w-12 h-12 rounded-sm bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] shrink-0">
            <Gift size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow mb-1 text-[#d4af37]">Programa de indicações</div>
            {refData.stats.unlocked > 0 ? (
              <div className="text-sm text-[#fdfbf7]">
                Tem <strong className="text-[#d4af37]">{refData.available_discount.toFixed(0)}€</strong> de desconto prontos a usar.
              </div>
            ) : refData.stats.pending > 0 ? (
              <div className="text-sm text-[#a0b2af]">
                {refData.stats.pending} {refData.stats.pending === 1 ? "indicação" : "indicações"} a aguardar o primeiro pagamento.
              </div>
            ) : (
              <div className="text-sm text-[#a0b2af]">
                Partilhe o seu código <span className="font-mono text-[#d4af37]">{refData.code}</span> e ganhe 5€ por cada cliente que se registe.
              </div>
            )}
          </div>
          <ChevronRight size={18} className="text-[#a0b2af] shrink-0" />
        </Link>
      )}

      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        <section className="lux-card p-5 md:p-8">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div className="font-display text-xl md:text-2xl">Próximas limpezas</div>
            <Link to="/cliente/reservas" className="text-xs text-[#d4af37] uppercase tracking-widest">Ver todas</Link>
          </div>
          {upcoming.length === 0 && (
            <div className="text-sm text-[#a0b2af]">Sem agendamentos. <Link to="/cliente/agendar" className="text-[#d4af37]">Agendar →</Link></div>
          )}
          <div className="space-y-3">
            {upcoming.map(b => (
              <div key={b.id} className={`p-4 rounded-sm ev-${b.status === "confirmed" ? "confirmed" : b.status === "in_progress" ? "confirmed" : "pending"}`} data-testid={`booking-${b.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest opacity-70">{b.apt_type} · {statusLabel(b.status)}</div>
                    <div className="text-sm mt-1">{b.scheduled_date} · {b.scheduled_time}</div>
                    {b.apartment_name && <div className="text-xs text-[#d4af37] mt-1">{b.apartment_name}</div>}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-[#d4af37]/80">A combinar</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="lux-card p-5 md:p-8">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div className="font-display text-xl md:text-2xl">Histórico de limpezas</div>
            <Link to="/cliente/historico" className="text-xs text-[#d4af37] uppercase tracking-widest">Ver tudo</Link>
          </div>
          <p className="text-sm text-[#a0b2af]">
            Acompanhe as limpezas concluídas, fotografias e relatórios na linha do tempo.
          </p>
          <Link to="/cliente/historico" className="btn-ghost-gold mt-5 inline-flex items-center gap-2 px-4 py-2 text-xs">
            Abrir linha do tempo <ChevronRight size={14} />
          </Link>
        </section>
      </div>
    </div>
  );
}

export function statusLabel(s) {
  return { pending: "Pendente", confirmed: "Confirmado", in_progress: "Em curso", completed: "Concluído", cancelled: "Cancelado" }[s] || s;
}
